import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBuilding, faLocationDot, faUserTie } from "@fortawesome/pro-regular-svg-icons"
import { faScreencast } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { MeetingListPagination } from "./MeetingListPagination"
import React from "react"
import { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import type { ServiceCenterMeetingRow } from "../../serviceCenterHomeTypes"
import { useListPagination } from "../../../../../../shared/hooks/useListPagination"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { getHostOrganizerCompanyName, getHostOrganizerName, toMeetingAudience, toMeetingRowTimes } from "./helpers"
import type { MeetingArrivals } from "./helpers"
import { MeetingArrivalsBadge } from "./MeetingArrivalsBadge"
import { MeetingAudienceChip } from "./MeetingAudienceChip"

const useStyles = makeStyles()(() => ({
    timeBox: {
        paddingLeft: "1rem",
        borderLeft: "3px solid #BFDBFE",
    },
    detailRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        color: tailwindPalette.slate[500],
        fontSize: "0.875rem",
    },
    divider: {
        marginTop: "0.5rem",
        borderColor: "#E2E8F0",
    },
    item: {
        padding: ".5rem",
        cursor: "pointer",
        marginBottom: ".1rem",
        marginTop: ".3rem",
        border: "1px solid transparent",
    },
    hoverBlue: {
        "&:hover": {
            borderRadius: ".75rem",
            border: `1px solid ${tailwindPalette.blue[500]}`,
            backgroundColor: tailwindPalette.blue[50],
        },
    },
    hoverGray: {
        "&:hover": {
            borderRadius: ".75rem",
            border: `1px solid ${tailwindPalette.slate[300]}`,
            backgroundColor: tailwindPalette.slate[100],
        },
    },
    activeItem: {
        borderRadius: "0.75rem",
        border: `1px solid ${tailwindPalette.blue[500]}`,
        backgroundColor: tailwindPalette.blue[50],
    },
}))

export function MeetingList({
    meetings = [],
    arrivals,
    selectedMeeting,
    onMeetingClick,
}: {
    meetings?: ReadonlyArray<ServiceCenterMeetingRow>
    arrivals?: ReadonlyMap<number, MeetingArrivals>
    onMeetingClick: (item: ServiceCenterBooking) => void
    selectedMeeting: ServiceCenterBooking | null
}) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { pagedItems, pagination } = useListPagination(meetings)

    return (
        <Stack spacing="1rem">
            {pagedItems.map((meeting) => {
                const times = toMeetingRowTimes(meeting.meetingDetails, dateTimeFactory)

                return (
                    <React.Fragment key={meeting.id}>
                        <Box
                            className={cx(
                                classes.item,
                                selectedMeeting?.id === meeting.id ? classes.hoverBlue : classes.hoverGray,
                                { [classes.activeItem]: selectedMeeting?.id === meeting.id },
                            )}
                            onClick={() => onMeetingClick(meeting)}
                        >
                            <Stack direction="row" alignItems="center" spacing="1rem">
                                <Box>
                                    <Typography variant="subtitle1">{times.start}</Typography>
                                    <Typography variant="body2" component="span" color="textSecondary">
                                        {times.end}
                                    </Typography>
                                </Box>

                                <Stack className={classes.timeBox} direction="column" spacing=".5rem">
                                    <Stack direction="row" alignItems="center" spacing=".5rem">
                                        <Typography variant="subtitle1">
                                            {meeting?.meetingDetails?.meetingName}
                                        </Typography>
                                        <LocalizedStrict
                                            id="service-center-reports-dashboard-meetings-list-row-shown-on-displays-tooltip"
                                            attrs={{ title: true }}
                                        >
                                            <Tooltip title="Shown on meeting displays">
                                                <FontAwesomeIcon icon={faScreencast} />
                                            </Tooltip>
                                        </LocalizedStrict>
                                        <MeetingAudienceChip
                                            audience={toMeetingAudience(meeting?.meetingDetails?.isExternal)}
                                        />
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing="1rem">
                                        <Box className={classes.detailRow}>
                                            <FontAwesomeIcon icon={faUserTie} />
                                            <Typography variant="body2">
                                                {getHostOrganizerName(meeting?.organizers)}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.detailRow}>
                                            <FontAwesomeIcon icon={faBuilding} />
                                            <Typography variant="body2">
                                                {getHostOrganizerCompanyName(meeting?.organizers)}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.detailRow}>
                                            <FontAwesomeIcon icon={faLocationDot} />
                                            <Typography variant="body2">
                                                {meeting?.meetingDetails?.location ?? "-"}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Stack>
                                {arrivals?.has(meeting.id) && (
                                    <Box marginLeft="auto" alignSelf="flex-start">
                                        <MeetingArrivalsBadge
                                            arrivals={arrivals.get(meeting.id) ?? { arrived: 0, total: 0 }}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                        <Divider className={classes.divider} />
                    </React.Fragment>
                )
            })}
            <MeetingListPagination {...pagination} />
        </Stack>
    )
}
